import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Copy, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Textarea,
  buttonStyles,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { GiftCard, GiftCardCreated, GiftCardDetail, StoreCreditView } from '../lib/types';

/**
 * Satış → Hediye kartları (F6-12, ADR-093): kart oluşturma (kod yalnızca bir kez gösterilir), bakiye ve hareketler,
 * kapatma, son kullanma tarihi ve gerekçeli bakiye düzeltme. Müşteri sayfasındaki "Mağaza kredisi" bölümü de
 * buradadır. Bakiye değiştiren her işlem iki adımlı doğrulama ister ve denetim kaydına düşer.
 */

type CardState = GiftCard['state'];
const STATES: CardState[] = ['active', 'disabled', 'expired', 'empty'];
const stateTone: Record<CardState, Tone> = {
  active: 'moss',
  disabled: 'slate',
  expired: 'amber',
  empty: 'neutral',
};
const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
const ROUTE = '/hediye-kartlari';

/** ISO tarih → yerel "YYYY-AA-GG" (tarih alanı için). */
function toDateInput(v: string | Date | null): string {
  if (!v) return '';
  const d = new Date(v);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** Son kullanma günü dahil: seçilen günün sonuna kadar geçerli. */
const endOfDay = (v: string) => (v ? new Date(`${v}T23:59:59`).toISOString() : null);

function signed(money: (a: number, c: string) => string, amountMinor: number, currency: string) {
  return `${amountMinor > 0 ? '+' : '−'}${money(Math.abs(amountMinor), currency)}`;
}

// ---------------------------------------------------------------------------- Liste

export function GiftCardsPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'' | CardState>('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<GiftCardCreated | null>(null);
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (state) params.set('status', state);
  const list = useQuery({
    queryKey: ['gift-cards', q.trim(), state],
    queryFn: () => api.get<GiftCard[]>(`/v1/admin/gift-cards?${params.toString()}`),
  });
  const qInvalid = !!q.trim() && !/^[0-9A-Za-z]{1,4}$/.test(q.trim());
  return (
    <PageFrame
      helpKey="page.gift_cards"
      actions={
        can('payments.giftcard.issue') && (
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.giftCards.create')}
            </Button>
            <HelpTip k="gift_cards.create" />
          </div>
        )
      }
    >
      <div className="space-y-6">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.giftCards.lead')}</p>
        <div className="grid max-w-xl gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.giftCards.search')}
            help={<HelpTip k="gift_cards.search" />}
            error={qInvalid ? t('admin.giftCards.searchInvalid') : undefined}
          >
            {(p) => (
              <Input
                {...p}
                value={q}
                maxLength={4}
                className="uppercase"
                placeholder={t('admin.giftCards.searchPlaceholder')}
                onChange={(e) => setQ(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.giftCards.stateFilter')} help={<HelpTip k="gift_cards.state_filter" />}>
            {(p) => (
              <Select {...p} value={state} onChange={(e) => setState(e.target.value as typeof state)}>
                <option value="">{t('common.all')}</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.giftCards.states.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        {qInvalid ? null : !list.data ? (
          <Spinner label={t('common.loading')} />
        ) : !list.data.length ? (
          <EmptyState
            title={q.trim() || state ? t('admin.giftCards.noMatch') : t('admin.giftCards.emptyTitle')}
            description={q.trim() || state ? undefined : t('admin.giftCards.emptyLead')}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[40rem] text-sm" aria-label={t('admin.giftCards.listLabel')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.giftCards.colCard')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.giftCards.colState')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.giftCards.colBalance')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.giftCards.colExpires')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.giftCards.colCreated')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => (
                  <tr key={c.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <Link
                        to={`${ROUTE}/$id`}
                        params={{ id: c.id }}
                        className="font-medium text-pine hover:underline"
                      >
                        {t('admin.giftCards.cardTitle', { last4: c.last4 })}
                      </Link>
                      {c.note && <p className="text-ink-3">{c.note}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={stateTone[c.state]}>{t(`admin.giftCards.states.${c.state}`)}</Badge>
                    </td>
                    <td className="px-3 py-2 text-end tabular">
                      {money(c.balanceMinor, c.currency)}
                      <span className="block text-ink-3">
                        {t('admin.giftCards.ofInitial', { amount: money(c.initialMinor, c.currency) })}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-end text-ink-2">
                      {c.expiresAt ? formatDate(c.expiresAt) : t('admin.giftCards.noExpiry')}
                    </td>
                    <td className="px-3 py-2 text-end text-ink-2">{formatDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && (
        <CreateGiftCardDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setCreated(r);
          }}
        />
      )}
      {created && <CodeOnceDialog created={created} onClose={() => setCreated(null)} />}
    </PageFrame>
  );
}

export function CreateGiftCardDialog({
  customerId,
  customerName,
  onClose,
  onCreated,
}: {
  customerId?: string;
  customerName?: string;
  onClose: () => void;
  onCreated: (r: GiftCardCreated) => void;
}) {
  const t = useT();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const amountMinor = toMinor(amount, currency);
  const create = useApiMutation(
    () =>
      api.post<GiftCardCreated>(
        '/v1/admin/gift-cards',
        {
          amountMinor: amountMinor ?? 0,
          currency,
          expiresAt: endOfDay(expires),
          note: note.trim() || null,
          ...(customerId ? { customerId } : {}),
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gift-cards'], ...(customerId ? [['gift-cards', 'customer', customerId]] : [])],
      onSuccess: (r) => onCreated(r),
      silentFieldErrors: true,
    },
  );
  const err = create.error;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.giftCards.createTitle')}
      description={
        customerName
          ? t('admin.giftCards.createForCustomer', { name: customerName })
          : t('admin.giftCards.createLead')
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!amountMinor || amountMinor <= 0}
            onClick={() => create.mutate(undefined)}
          >
            {t('admin.giftCards.createButton')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
          <Field
            label={t('admin.giftCards.amount')}
            required
            help={<HelpTip k="gift_cards.amount" />}
            error={
              fieldError(err, 'amountMinor') ??
              (amount.trim() && amountMinor === null ? t('admin.giftCards.amountInvalid') : undefined)
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="tabular"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.giftCards.currency')} help={<HelpTip k="gift_cards.currency" />}>
            {(p) => (
              <Select {...p} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field
          label={t('admin.giftCards.expiresAt')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="gift_cards.expires_at" />}
          description={t('admin.giftCards.expiresHint')}
          error={fieldError(err, 'expiresAt')}
        >
          {(p) => <Input {...p} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.giftCards.note')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="gift_cards.note" />}
          description={t('admin.giftCards.noteHint')}
          error={fieldError(err, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              className="min-h-16"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {err && !err.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {err.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Yeni kartın kodu: yalnızca bu pencerede gösterilir; sunucuda yalnızca özeti durur. */
function CodeOnceDialog({ created, onClose }: { created: GiftCardCreated; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const c = created.giftCard;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.code);
      toast.success(t('admin.giftCards.copied'));
    } catch {
      toast.error(t('admin.giftCards.copyFailed'));
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.giftCards.createdTitle')}
      description={t('admin.giftCards.createdLead', { amount: money(c.balanceMinor, c.currency) })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Link to={`${ROUTE}/$id`} params={{ id: c.id }} className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.giftCards.openCard')}
          </Link>
          <Button variant="primary" onClick={onClose}>
            {t('admin.giftCards.savedCode')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.giftCards.code')}>
          {(p) => (
            <Input
              {...p}
              readOnly
              value={created.code}
              className="font-mono text-lg tracking-wider"
              onFocus={(e) => e.target.select()}
              data-testid="gift-card-code"
            />
          )}
        </Field>
        <div className="flex items-center gap-1">
          <Button onClick={() => void copy()}>
            <Copy className="h-4 w-4" aria-hidden />
            {t('admin.giftCards.copy')}
          </Button>
          <HelpTip k="gift_cards.copy_code" />
        </div>
        <p
          className="rounded-sm border border-amber/40 bg-amber-tint px-3 py-2 text-sm text-ink"
          role="status"
        >
          {t('admin.giftCards.codeOnce')}
        </p>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Ayrıntı

export function GiftCardDetailPage() {
  const { id } = useParams({ from: '/app/hediye-kartlari/$id' });
  const t = useT();
  const q = useQuery({
    queryKey: ['gift-card', id],
    queryFn: () => api.get<GiftCardDetail>(`/v1/admin/gift-cards/${id}`),
  });
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <GiftCardDetailView key={`${q.data.id}:${q.data.version}`} card={q.data} />;
}

function GiftCardDetailView({ card: c }: { card: GiftCardDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canIssue = can('payments.giftcard.issue');
  const [expires, setExpires] = useState(toDateInput(c.expiresAt));
  const [note, setNote] = useState(c.note ?? '');
  const [adjusting, setAdjusting] = useState(false);
  const invalidate = [['gift-card', c.id], ['gift-cards']];
  const save = useApiMutation(
    (body: Record<string, unknown>) =>
      api.patch(`/v1/admin/gift-cards/${c.id}`, { ...body, version: c.version }),
    { invalidate, success: t('common.saved'), silentFieldErrors: true },
  );
  const dirty = expires !== toDateInput(c.expiresAt) || note.trim() !== (c.note ?? '');
  return (
    <PageFrame
      helpKey="page.gift_card_detail"
      title={t('admin.giftCards.cardTitle', { last4: c.last4 })}
      actions={
        <Link to={ROUTE} className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.giftCards.backToList')}
        </Link>
      }
    >
      <Section
        title={t('admin.giftCards.summary')}
        action={<Badge tone={stateTone[c.state]}>{t(`admin.giftCards.states.${c.state}`)}</Badge>}
      >
        <KeyValue
          items={[
            {
              label: t('admin.giftCards.balance'),
              value: <span className="tabular text-lg font-medium">{money(c.balanceMinor, c.currency)}</span>,
            },
            { label: t('admin.giftCards.initial'), value: money(c.initialMinor, c.currency) },
            {
              label: t('admin.giftCards.expiresAt'),
              value: c.expiresAt ? formatDate(c.expiresAt) : t('admin.giftCards.noExpiry'),
            },
            {
              label: t('admin.giftCards.customer'),
              value:
                c.customerId && c.customer ? (
                  <Link
                    to="/musteriler/$id"
                    params={{ id: c.customerId }}
                    className="text-pine hover:underline"
                  >
                    {c.customer.name ?? c.customer.email ?? '—'}
                  </Link>
                ) : (
                  '—'
                ),
            },
            { label: t('admin.giftCards.created'), value: formatDate(c.createdAt, true) },
          ]}
        />
        {canIssue && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1">
              <Button onClick={() => setAdjusting(true)}>{t('admin.giftCards.adjust')}</Button>
              <HelpTip k="gift_cards.adjust" />
            </span>
            <span className="flex items-center gap-1">
              <ConfirmButton
                variant={c.status === 'active' ? 'danger' : 'secondary'}
                size="md"
                message={
                  c.status === 'active'
                    ? t('admin.giftCards.disableConfirm')
                    : t('admin.giftCards.enableConfirm')
                }
                onConfirm={() => save.mutateAsync({ status: c.status === 'active' ? 'disabled' : 'active' })}
              >
                {c.status === 'active' ? t('admin.giftCards.disable') : t('admin.giftCards.enable')}
              </ConfirmButton>
              <HelpTip k="gift_cards.toggle" />
            </span>
          </div>
        )}
      </Section>
      {canIssue && (
        <Section title={t('admin.giftCards.settings')}>
          <div className="grid max-w-xl gap-4">
            <Field
              label={t('admin.giftCards.expiresAt')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="gift_cards.expires_at" />}
              description={t('admin.giftCards.expiresHint')}
              error={fieldError(save.error, 'expiresAt')}
            >
              {(p) => (
                <Input {...p} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
              )}
            </Field>
            <Field
              label={t('admin.giftCards.note')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="gift_cards.note" />}
              description={t('admin.giftCards.noteHint')}
            >
              {(p) => (
                <Textarea
                  {...p}
                  className="min-h-16"
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <div className="flex items-center gap-1">
              <Button
                variant="primary"
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate({ expiresAt: endOfDay(expires), note: note.trim() || null })}
              >
                {t('common.save')}
              </Button>
              <HelpTip k="gift_cards.save" />
            </div>
          </div>
        </Section>
      )}
      <Section title={t('admin.giftCards.history')}>
        {!c.transactions.length ? (
          <p className="text-sm text-ink-3">{t('admin.giftCards.noHistory')}</p>
        ) : (
          <TransactionTable
            label={t('admin.giftCards.history')}
            rows={c.transactions.map((x) => ({
              ...x,
              currency: c.currency,
              kindLabel: t(`admin.giftCards.kinds.${x.kind}`),
            }))}
          />
        )}
      </Section>
      {adjusting && (
        <AdjustDialog
          title={t('admin.giftCards.adjustTitle', { last4: c.last4 })}
          lead={t('admin.giftCards.adjustLead', { balance: money(c.balanceMinor, c.currency) })}
          url={`/v1/admin/gift-cards/${c.id}/adjust`}
          currency={c.currency}
          invalidate={invalidate}
          onClose={() => setAdjusting(false)}
        />
      )}
    </PageFrame>
  );
}

function TransactionTable({
  label,
  rows,
}: {
  label: string;
  rows: Array<{
    id: string;
    kindLabel: string;
    amountMinor: number;
    balanceAfterMinor: number;
    currency: string;
    orderId: string | null;
    orderNumber: string | null;
    note: string | null;
    createdAt: string;
  }>;
}) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full min-w-[36rem] text-sm" aria-label={label}>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.giftCards.colDate')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.giftCards.colKind')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.giftCards.colAmount')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.giftCards.colAfter')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id} className="border-t border-line align-top">
              <td className="px-3 py-2 text-ink-2">{formatDate(x.createdAt, true)}</td>
              <td className="px-3 py-2">
                {x.kindLabel}
                {x.orderId && x.orderNumber && (
                  <>
                    {' · '}
                    <Link
                      to="/siparisler/$id"
                      params={{ id: x.orderId }}
                      className="text-pine underline underline-offset-2 hover:no-underline"
                    >
                      {x.orderNumber}
                    </Link>
                  </>
                )}
                {x.note && <p className="text-ink-3">{x.note}</p>}
              </td>
              <td className={`px-3 py-2 text-end tabular ${x.amountMinor < 0 ? 'text-ink-2' : 'text-moss'}`}>
                {signed(money, x.amountMinor, x.currency)}
              </td>
              <td className="px-3 py-2 text-end tabular">{money(x.balanceAfterMinor, x.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Gerekçeli bakiye düzeltme (hediye kartı veya mağaza kredisi): ekle/düş + tutar + açıklama. */
function AdjustDialog({
  title,
  lead,
  url,
  currency: fixedCurrency,
  currencies,
  invalidate,
  onClose,
}: {
  title: string;
  lead: string;
  url: string;
  currency?: string;
  currencies?: string[];
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(fixedCurrency ?? currencies?.[0] ?? 'TRY');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const minor = toMinor(amount, currency);
  const m = useApiMutation(
    () =>
      api.post(
        url,
        {
          amountMinor: direction === 'add' ? (minor ?? 0) : -(minor ?? 0),
          note: note.trim(),
          ...(fixedCurrency ? {} : { currency }),
        },
        { idempotencyKey: idem },
      ),
    { invalidate, success: t('admin.giftCards.adjusted'), onSuccess: onClose, silentFieldErrors: true },
  );
  const ready = !!minor && minor > 0 && note.trim().length >= 3;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={title}
      description={lead}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={direction === 'add' ? 'primary' : 'danger'}
            loading={m.isPending}
            disabled={!ready}
            onClick={() => m.mutate(undefined)}
          >
            {direction === 'add' ? t('admin.giftCards.adjustAdd') : t('admin.giftCards.adjustSubtract')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.giftCards.direction')} help={<HelpTip k="tenders.adjust_direction" />}>
          {(p) => (
            <Select
              {...p}
              value={direction}
              onChange={(e) => setDirection(e.target.value as typeof direction)}
            >
              <option value="add">{t('admin.giftCards.directionAdd')}</option>
              <option value="subtract">{t('admin.giftCards.directionSubtract')}</option>
            </Select>
          )}
        </Field>
        <div className={fixedCurrency ? '' : 'grid gap-3 sm:grid-cols-[1fr_8rem]'}>
          <Field
            label={t('admin.giftCards.adjustAmount', { currency })}
            required
            help={<HelpTip k="tenders.adjust_amount" />}
            error={
              fieldError(m.error, 'amountMinor') ??
              (amount.trim() && minor === null ? t('admin.giftCards.amountInvalid') : undefined)
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="tabular"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          {!fixedCurrency && (
            <Field
              label={t('admin.giftCards.currency')}
              help={<HelpTip k="customers.store_credit_currency" />}
            >
              {(p) => (
                <Select {...p} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>
        <Field
          label={t('admin.giftCards.adjustNote')}
          required
          help={<HelpTip k="tenders.adjust_note" />}
          description={t('admin.giftCards.adjustNoteHint')}
          error={fieldError(m.error, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              className="min-h-16"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Müşteri sayfası

/**
 * Müşteri → Mağaza kredisi ve hediye kartları: bakiye (para birimine göre), hareketler, gerekçeli kredi ekleme/düşme
 * ve müşteriye bağlı hediye kartı verme.
 */
export function CustomerCreditSection({
  customerId,
  customerName,
  anonymized,
}: {
  customerId: string;
  customerName: string;
  anonymized: boolean;
}) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [adjusting, setAdjusting] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [created, setCreated] = useState<GiftCardCreated | null>(null);
  const credit = useQuery({
    queryKey: ['store-credit', customerId],
    queryFn: () => api.get<StoreCreditView>(`/v1/admin/customers/${customerId}/store-credit`),
  });
  const canCards = can('payments.giftcard.read');
  const cards = useQuery({
    queryKey: ['gift-cards', 'customer', customerId],
    queryFn: () => api.get<GiftCard[]>(`/v1/admin/gift-cards?customerId=${customerId}`),
    enabled: canCards,
  });
  const canManage = can('customers.credit.manage') && !anonymized;
  const canIssue = can('payments.giftcard.issue') && !anonymized;
  const balances = credit.data?.balances.filter((b) => b.balanceMinor !== 0) ?? [];
  return (
    <Section
      title={t('admin.storeCredit.title')}
      description={t('admin.storeCredit.lead')}
      action={
        <div className="flex flex-wrap items-center gap-1">
          {canManage && (
            <>
              <Button size="sm" onClick={() => setAdjusting(true)}>
                {t('admin.storeCredit.adjust')}
              </Button>
              <HelpTip k="customers.store_credit_adjust" />
            </>
          )}
          {canIssue && (
            <>
              <Button size="sm" onClick={() => setIssuing(true)}>
                {t('admin.storeCredit.issueGiftCard')}
              </Button>
              <HelpTip k="customers.gift_card_issue" />
            </>
          )}
        </div>
      }
    >
      {!credit.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-4">
          <p className="text-sm" data-testid="store-credit-balance">
            {balances.length ? (
              balances.map((b) => (
                <span key={b.currency} className="me-4 inline-block text-lg font-medium tabular">
                  {money(b.balanceMinor, b.currency)}
                </span>
              ))
            ) : (
              <span className="text-ink-3">{t('admin.storeCredit.none')}</span>
            )}
          </p>
          {credit.data.transactions.length > 0 && (
            <TransactionTable
              label={t('admin.storeCredit.history')}
              rows={credit.data.transactions.map((x) => ({
                ...x,
                kindLabel: t(`admin.storeCredit.kinds.${x.kind}`),
              }))}
            />
          )}
          {canCards && cards.data && cards.data.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium">{t('admin.storeCredit.giftCards')}</p>
              <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
                {cards.data.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                    <Link
                      to={`${ROUTE}/$id`}
                      params={{ id: c.id }}
                      className="min-w-0 flex-1 font-medium text-pine hover:underline"
                    >
                      {t('admin.giftCards.cardTitle', { last4: c.last4 })}
                    </Link>
                    <span className="tabular">{money(c.balanceMinor, c.currency)}</span>
                    <span className="text-ink-3">
                      {c.expiresAt ? formatDate(c.expiresAt) : t('admin.giftCards.noExpiry')}
                    </span>
                    <Badge tone={stateTone[c.state]}>{t(`admin.giftCards.states.${c.state}`)}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {adjusting && (
        <AdjustDialog
          title={t('admin.storeCredit.adjustTitle')}
          lead={t('admin.storeCredit.adjustLead')}
          url={`/v1/admin/customers/${customerId}/store-credit`}
          currencies={[...(credit.data?.balances.map((b) => b.currency) ?? []), 'TRY'].filter(
            (c, i, a) => a.indexOf(c) === i,
          )}
          invalidate={[
            ['store-credit', customerId],
            ['customer-360', customerId],
          ]}
          onClose={() => setAdjusting(false)}
        />
      )}
      {issuing && (
        <CreateGiftCardDialog
          customerId={customerId}
          customerName={customerName}
          onClose={() => setIssuing(false)}
          onCreated={(r) => {
            setIssuing(false);
            setCreated(r);
          }}
        />
      )}
      {created && <CodeOnceDialog created={created} onClose={() => setCreated(null)} />}
    </Section>
  );
}
